import { ApiEnvironment, API_URLS, readApiEnvironment, getApiBaseUrl } from '../config/env';

export interface AuthSession { accessToken: string; refreshToken: string; expiresAt: number; }
export interface SavedSession extends AuthSession {
  id: string; email: string; environment: ApiEnvironment; apiUrl: string; needsLogin?: boolean;
}
export const SESSION_STORAGE_KEY = 'golden-hounds-admin-session';
export const LOGIN_EMAIL_STORAGE_KEY = 'golden-hounds-admin-login-email';
export const ACTIVE_SESSION_KEY = 'golden-hounds-active-session';
export const SESSION_PREFIX = 'golden-hounds-session:';
export type ClearReason = string;
type Subscriber = (session: SavedSession | null, reason?: string) => void;
const subscribers = new Set<Subscriber>();
let listening = false;
let activationVersion = 0;
export function sessionVersion(): number { return activationVersion; }
export function normalizeApiUrl(value: string): string {
  const url = new URL(value);
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.search || url.hash) {
    throw new Error('Use an HTTP or HTTPS API URL without credentials, query or fragment');
  }
  return url.toString().replace(/\/+$/, '');
}
function validTokens(value: any): value is AuthSession {
  return value && typeof value.accessToken === 'string' && !!value.accessToken &&
    typeof value.refreshToken === 'string' && !!value.refreshToken && Number.isFinite(value.expiresAt);
}
export function readSavedSession(id: string): SavedSession | null {
  try {
    const value = JSON.parse(localStorage.getItem(SESSION_PREFIX + id) || 'null');
    if (!value || value.id !== id || typeof value.email !== 'string' ||
      !(value.environment === 'other' || Object.prototype.hasOwnProperty.call(API_URLS, value.environment)) ||
      normalizeApiUrl(value.apiUrl) !== value.apiUrl || (!value.needsLogin && !validTokens(value))) return null;
    return value;
  } catch { return null; }
}
export function listSessions(): SavedSession[] {
  return Object.keys(localStorage).filter(key => key.startsWith(SESSION_PREFIX))
    .map(key => readSavedSession(key.slice(SESSION_PREFIX.length)))
    .filter((value): value is SavedSession => !!value)
    .sort((a, b) => a.environment.localeCompare(b.environment) || a.email.localeCompare(b.email));
}
export function activeSessionId(): string | null { return sessionStorage.getItem(ACTIVE_SESSION_KEY); }
export function readSession(): SavedSession | null {
  const id = activeSessionId();
  const session = id ? readSavedSession(id) : null;
  return session && !session.needsLogin ? session : null;
}
function notify(reason?: string) { subscribers.forEach(cb => cb(readSession(), reason)); }
export function activateSession(id: string | null): void {
  activationVersion += 1;
  if (id) sessionStorage.setItem(ACTIVE_SESSION_KEY, id);
  else sessionStorage.removeItem(ACTIVE_SESSION_KEY);
  notify();
}
export function saveSession(session: SavedSession): void {
  localStorage.setItem(SESSION_PREFIX + session.id, JSON.stringify(session));
  notify();
}
export function writeSession(tokens: AuthSession): void {
  const current = readSession();
  const session = current ? { ...current, ...tokens } : {
    ...tokens, id: encodeURIComponent(normalizeApiUrl(getApiBaseUrl()) + '\n' + (localStorage.getItem(LOGIN_EMAIL_STORAGE_KEY) || 'Existing session')), email: localStorage.getItem(LOGIN_EMAIL_STORAGE_KEY) || 'Existing session',
    environment: readApiEnvironment(), apiUrl: normalizeApiUrl(getApiBaseUrl()),
  };
  saveSession(session);
  if (!current) activateSession(session.id);
}
export function clearSession(reason?: string, expectedRefreshToken?: string, id = activeSessionId()): void {
  if (!id) return;
  const current = readSavedSession(id);
  if (!current || (expectedRefreshToken && current.refreshToken !== expectedRefreshToken)) return;
  if (reason === 'session-expired' || reason === 'admin-required') {
    localStorage.setItem(SESSION_PREFIX + id, JSON.stringify({ ...current, accessToken: '', refreshToken: '', expiresAt: 0, needsLogin: true }));
  } else localStorage.removeItem(SESSION_PREFIX + id);
  notify(id === activeSessionId() ? reason : undefined);
}
export function migrateLegacySession(): void {
  const raw = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return;
  try {
    const tokens = JSON.parse(raw);
    if (validTokens(tokens) && listSessions().length === 0) writeSession(tokens);
  } catch { /* Discard malformed legacy storage. */ } finally { localStorage.removeItem(SESSION_STORAGE_KEY); }
}
export function subscribeSession(callback: Subscriber): () => void {
  if (!listening) {
    window.addEventListener('storage', event => {
      if (event.key === null || event.key.startsWith(SESSION_PREFIX)) notify();
    });
    listening = true;
  }
  subscribers.add(callback);
  return () => { subscribers.delete(callback); };
}
